"use client";

import { useQuery } from "@tanstack/react-query";

export type VolumeBar = [number, number];

export interface SmartRow {
  smart_wallet_count: number;
  smart_volume: number;
  net_buyers_pct: number;
  avg_smart_roi: number;
  avg_smart_win_rate: number;
  title?: string;
  market_slug?: string;
}

export interface FooterBulk {
  oi: Map<string, number>;
  volume: Map<string, VolumeBar[]>;
  smart: Map<string, SmartRow>;
}

async function fetchFooterBulk(cids: string[]): Promise<FooterBulk> {
  const empty: FooterBulk = { oi: new Map(), volume: new Map(), smart: new Map() };
  if (cids.length === 0) return empty;
  const qs = new URLSearchParams({ cids: cids.join(",") });
  const res = await fetch(`/api/analytics/markets/footer-bulk?${qs.toString()}`);
  if (!res.ok) return empty;
  const json = await res.json();
  const data = (json?.data || {}) as {
    oi?: Record<string, number>;
    volume?: Record<string, VolumeBar[]>;
    smart?: Record<string, SmartRow>;
  };

  const oi = new Map<string, number>();
  for (const [k, v] of Object.entries(data.oi || {})) {
    if (typeof v === "number" && Number.isFinite(v)) oi.set(k.toLowerCase(), v);
  }
  const volume = new Map<string, VolumeBar[]>();
  for (const [k, v] of Object.entries(data.volume || {})) {
    if (Array.isArray(v) && v.length >= 2) volume.set(k.toLowerCase(), v);
  }
  const smart = new Map<string, SmartRow>();
  for (const [k, v] of Object.entries(data.smart || {})) {
    if (v && typeof v === "object") smart.set(k.toLowerCase(), v as SmartRow);
  }
  return { oi, volume, smart };
}

/**
 * Unified bulk fetch for the EventCard footer — OI + 24h volume bars +
 * smart money activity in a SINGLE HTTP round trip. Replaces the three
 * separate hooks (useOpenInterestMap, useVolumeHistoryMap, useSmartActivityMap)
 * on the markets list view so the footer paints in one transition instead of
 * three staggered pop-ins.
 */
export function useFooterBulk(conditionIds: string[], enabled = true) {
  const key = Array.from(new Set(conditionIds.map((c) => c.toLowerCase()))).sort().join(",");
  return useQuery<FooterBulk>({
    queryKey: ["analytics", "footer-bulk", key],
    queryFn: () => fetchFooterBulk(conditionIds),
    // Skip under HIDE_PREDEXON — OI, volume bars and smart-money in the
    // footer-bulk all come back empty during the Predexon outage, so drop the
    // whole footer rather than paint empty cells on every card.
    enabled: enabled && conditionIds.length > 0 && process.env.NEXT_PUBLIC_HIDE_PREDEXON_TABS !== "true",
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: false,
  });
}
